import { createContext, useContext, useMemo, useState } from 'react';
import type {
  Placement,
  UseClickProps,
  UseDismissProps,
  UseFloatingOptions,
  UseRoleProps,
} from '@floating-ui/react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';

export type PopoverOptions = Partial<UseFloatingOptions> & {
  /** Option to close Floating UI when clicked anywhere inside */
  autoClose?: boolean;
  /** Options to pass to Floating UI's useClick hook. Can be used to disable the interaction. */
  clickOptions?: UseClickProps;
  /** Options to pass to Floating UI's useDismiss hook. Can be used to disable the interaction. */
  dismissOptions?: UseDismissProps;
  /** Whether the popover is initially open. Is overridden by open prop. */
  initialOpen?: boolean;
  /** Whether the popover is modal. */
  modal?: boolean;
  /** If passed, the Popover's open state is controlled externally. Used with open prop. */
  onOpenChange?(open: boolean): void;
  /** If passed, the Popover's open state is controlled externally. Used with onOpenChange prop. Overrides initialOpen prop. */
  open?: boolean;
  /** The placement of the popover. */
  placement?: Placement;
  /** Options to pass to Floating UI's useRole hook. Can be used to disable the interaction. */
  roleOptions?: UseRoleProps;
};

const defaultMiddleware = [
  offset(2),
  flip({
    fallbackAxisSideDirection: 'end',
  }),
  shift({ padding: 5 }),
];

export const usePopover = ({
  autoClose,
  clickOptions = {},
  dismissOptions = {},
  initialOpen = false,
  middleware = defaultMiddleware,
  modal = false,
  onOpenChange,
  open,
  placement,
  roleOptions = {},
  whileElementsMounted = autoUpdate,
  ...useFloatingProps
}: PopoverOptions = {}): any => {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const { context, ...data } = useFloating({
    middleware,
    onOpenChange: onOpenChange || setIsOpen,
    open: open || isOpen,
    placement,
    whileElementsMounted,
    ...useFloatingProps,
  });

  const click = useClick(context, clickOptions);
  const dismiss = useDismiss(context, dismissOptions);
  const role = useRole(context, roleOptions);

  const interactions = useInteractions([click, dismiss, role]);

  return useMemo(
    () => ({
      autoClose,
      open: open || isOpen,
      onOpenChange: onOpenChange || setIsOpen,
      context,
      modal,
      ...interactions,
      ...data,
    }),
    [autoClose, open, isOpen, onOpenChange, context, interactions, data, modal]
  );
};

type ContextType = ReturnType<typeof usePopover> | null;

export const PopoverContext = createContext<ContextType>(null);

export const usePopoverContext = (): any => {
  const context = useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};
